import { QueryField } from '../../../../../types/QueryBuilder';
import { Translate } from '../ValueEditors/valueUtils';

const CATEGORY_KEY_PREFIX = 'pages.clients.queryBuilder.simple.categories';
const OTHER_CATEGORY_KEY = `${CATEGORY_KEY_PREFIX}.other`;
export const PURCHASE_BEHAVIOR_CATEGORY_TITLE_KEY = `${CATEGORY_KEY_PREFIX}.purchaseBehavior`;
export const GDPR_CATEGORY_TITLE_KEY = `${CATEGORY_KEY_PREFIX}.gdpr`;
export const CLIENT_LISTS_CATEGORY_TITLE_KEY = `${CATEGORY_KEY_PREFIX}.clientLists`;

export const STORE_FIELD_NAME = 'PERSON_ENTITY';
export const PURCHASE_PERIOD_FIELD_NAME = 'TRANSACTION_PERIOD';
export const PURCHASE_COUNT_FIELD_NAME = 'TRANSACTION_NB';
export const COUNTER_FIELD_NAME = 'CUSTOMER_ACCOUNT';
export const COUNTER_TYPE_FIELD_NAME = 'CUSTOMER_ACCOUNT_TYPE';
export const IDENTIFIER_LIST_FIELD_NAME = 'CUSTOMER_IDENTIFIER';
export const CHANNEL_PRIORITY_FIELD_NAME = 'CHANNEL_PRIORITY';
export const COUNTRY_FIELD_NAME = 'CUSTOMER_COUNTRY';

export const OPTIN_TO_STATUS_FIELD_NAME: Record<string, string> = {
    CUSTOMER_OPTIN_EMAIL: 'CUSTOMER_STATUS_EMAIL',
    CUSTOMER_OPTIN_SMS: 'CUSTOMER_STATUS_SMS',
    CUSTOMER_OPTIN_COURRIER: 'CUSTOMER_STATUS_COURRIER',
};

export const STATUS_FIELD_NAMES = Object.values(OPTIN_TO_STATUS_FIELD_NAME);

export const PAIRED_FIELD_NAME: Record<string, string> = {
    [COUNTER_FIELD_NAME]: COUNTER_TYPE_FIELD_NAME,
    [PURCHASE_PERIOD_FIELD_NAME]: PURCHASE_COUNT_FIELD_NAME,
    ...OPTIN_TO_STATUS_FIELD_NAME,
};

export const MERGED_INTO_FIELD_NAMES = new Set(Object.values(PAIRED_FIELD_NAME));

export const NOMENCLATURE_FIELD_NAMES = Array.from({ length: 6 }, (_, i) => `TRANSACTION_NOMENCLATURE_LEVEL_${i + 1}`);

export const GEOGRAPHY_FIELD_NAMES = ['CUSTOMER_CITY', 'CUSTOMER_DEPARTMENT', COUNTRY_FIELD_NAME];

export const DIALOG_SELECT_FIELD_NAMES = new Set([STORE_FIELD_NAME, ...GEOGRAPHY_FIELD_NAMES, ...NOMENCLATURE_FIELD_NAMES]);

export const REMOTE_SEARCH_FIELD_CRITERIA: Record<string, string> = {
    ITEM_PURCHASE_REFERENCE: 'articles',
    CLASSIFICATION_PRODUCT: 'articles',
};

export const CLIENT_LIST_FIELD_NAMES = ['CUSTOMER_LIST_BY_IDENTIFIER', 'CUSTOMER_LIST_BY_CARTE'];

export const DUAL_LIST_NO_SEARCH_FIELD_NAMES = new Set([IDENTIFIER_LIST_FIELD_NAME, 'PERSON_CATEGORY']);

export const DUAL_LIST_FIELD_NAMES = new Set([...DUAL_LIST_NO_SEARCH_FIELD_NAMES, ...CLIENT_LIST_FIELD_NAMES]);

export const ROW_SPAN_2_FIELD_NAMES = new Set(['CUSTOMER_SCORE', STORE_FIELD_NAME]);

export const FIELD_GRID_POSITION: Record<string, { column: number; row: number }> = {
    PERSON_ENTITY: { column: 1, row: 1 },
    CUSTOMER_IDENTIFIER: { column: 2, row: 1 },
    CUSTOMER_ACTIVATION_DATE: { column: 2, row: 2 },
    CUSTOMER_SUBSCRIPTIONS_TYPE: { column: 1, row: 3 },
    CUSTOMER_ACCOUNT: { column: 2, row: 3 },
};

const FIELD_LABEL_KEYS: Record<string, string> = {
    PERSON_ENTITY: 'stores',
    CUSTOMER_IDENTIFIER: 'identifiers',
    CUSTOMER_ACTIVATION_DATE: 'cardOpening',
    CUSTOMER_SUBSCRIPTIONS_TYPE: 'subscriptionsType',
    CUSTOMER_ACCOUNT: 'customerAccount',
    CUSTOMER_ACCOUNT_TYPE: 'accountType',
    CUSTOMER_SCORE: 'customerScore',
    CUSTOMER_SCORE_BEST: 'bestCustomers',
    TRANSACTION_NB: 'purchaseCount',
    TRANSACTION_PERIOD: 'purchasePeriod',
    CUSTOMER_OPTIN_EMAIL: 'optinEmail',
    CUSTOMER_STATUS_EMAIL: 'statusEmail',
    CUSTOMER_OPTIN_SMS: 'optinSms',
    CUSTOMER_STATUS_SMS: 'statusSms',
    CUSTOMER_OPTIN_COURRIER: 'optinCourrier',
    CUSTOMER_STATUS_COURRIER: 'statusCourrier',
    CUSTOMER_CITY: 'city',
    CUSTOMER_DEPARTMENT: 'department',
    CUSTOMER_COUNTRY: 'country',
    CHANNEL_PRIORITY: 'channelPriority',
    CUSTOMER_LIST_BY_IDENTIFIER: 'listByIdentifier',
    CUSTOMER_LIST_BY_CARTE: 'listByCard',
    PERSON_CATEGORY: 'relationalCriteria',
};

export const resolveFieldLabel = (field: QueryField, t: Translate): string => {
    const key = FIELD_LABEL_KEYS[field.name];
    return key ? t(`pages.clients.queryBuilder.simple.fieldLabels.${key}`) : field.label;
};

const SUBCATEGORIES: { titleKey: string; fieldNames: string[] }[] = [
    {
        titleKey: `${CATEGORY_KEY_PREFIX}.clientCategory`,
        fieldNames: [STORE_FIELD_NAME, IDENTIFIER_LIST_FIELD_NAME, 'CUSTOMER_ACTIVATION_DATE', 'CUSTOMER_SUBSCRIPTIONS_TYPE', COUNTER_FIELD_NAME, COUNTER_TYPE_FIELD_NAME],
    },
    {
        titleKey: `${CATEGORY_KEY_PREFIX}.clientScore`,
        fieldNames: ['CUSTOMER_SCORE', 'CUSTOMER_SCORE_BEST', 'PERSON_CATEGORY'],
    },
    {
        titleKey: PURCHASE_BEHAVIOR_CATEGORY_TITLE_KEY,
        fieldNames: [...NOMENCLATURE_FIELD_NAMES, ...Object.keys(REMOTE_SEARCH_FIELD_CRITERIA), PURCHASE_COUNT_FIELD_NAME, PURCHASE_PERIOD_FIELD_NAME],
    },
    {
        titleKey: GDPR_CATEGORY_TITLE_KEY,
        fieldNames: [
            ...Object.entries(OPTIN_TO_STATUS_FIELD_NAME).flat(),
            CHANNEL_PRIORITY_FIELD_NAME,
        ],
    },
    {
        titleKey: `${CATEGORY_KEY_PREFIX}.geography`,
        fieldNames: GEOGRAPHY_FIELD_NAMES,
    },
    {
        titleKey: CLIENT_LISTS_CATEGORY_TITLE_KEY,
        fieldNames: CLIENT_LIST_FIELD_NAMES,
    },
];

const FIELD_POSITION = new Map<string, { titleKey: string; order: number }>(
    SUBCATEGORIES.flatMap(({ titleKey, fieldNames }) => fieldNames.map((name, order) => [name, { titleKey, order }] as const))
);

interface FieldSubcategory {
    titleKey: string;
    fields: QueryField[];
}

export function groupFieldsBySubcategory(fields: QueryField[]): FieldSubcategory[] {
    const titleKeys = [...SUBCATEGORIES.map(s => s.titleKey), OTHER_CATEGORY_KEY];
    const orderOf = (field: QueryField) => FIELD_POSITION.get(field.name)?.order ?? 0;

    return titleKeys
        .map(titleKey => ({
            titleKey,
            fields: fields
                .filter(field => (FIELD_POSITION.get(field.name)?.titleKey ?? OTHER_CATEGORY_KEY) === titleKey)
                .sort((a, b) => orderOf(a) - orderOf(b)),
        }))
        .filter(group => group.fields.length > 0);
}

export const toHelpKey = (titleKey: string): string => titleKey.replace('.categories.', '.categoryHelp.');
